import './default.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import PaidAlertModal from '@/resources/components/cart/paidAlertModal/PaidAlertModal';
import SendOrderAlert from '@/resources/components/cart/sendOrderAlert/SendOrderAlert';
import Shipment from '@/resources/components/cart/shipment/Shipment';
import StatusSteps from '@/resources/components/cart/statusSteps/StatusSteps';
import CartPageLoad from '@/resources/components/loads/CartPageLoad';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useInitiateCheckoutPixel } from '@/resources/hooks/useInitiateCheckoutPixel';
import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { usePageBreakpoints } from '@/resources/hooks/usePageBreakpoints';
import { usePurchasePixel } from '@/resources/hooks/usePurchasePixel';
import { useSmartTextColor } from '@/resources/hooks/useSmartTextColor';
import { useTextColor } from '@/resources/hooks/useTextColor';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { ApplicationState } from '@/store';
import { ICartProduct } from '@/store/ducks/order/types';
import { IPaymentAlertAsks } from '@/store/ducks/payment/types';
import { IProductList } from '@/store/ducks/productLists/types';
import { IAlert } from '@/store/ducks/products/types';

import { canChangeOrder, canFinishOrder, OrderStatus } from '../../../../services/global';
import { PbmAlert } from '../../../components/product/pbmPromotions/PbmAlert';
import { ComboAddModal } from '../../product/default/combos/components/ComboAddModal';
import { ComboBottomSheet } from '../../product/default/combos/components/ComboBottomSheet';
import { ComboModal } from '../../product/default/combos/components/ComboModal';
import { ComboRemoveItemModal } from '../../product/default/combos/components/ComboRemoveItemModal';
import { AddMoreItems, CartContent, EmptyCart, FreeShippingSection } from './components';
import { CartContextStepper } from './components/CartContextStepper';
import { useCartData, useCartEffects, useCartModals } from './hooks';
import { shouldShowStatusSteps } from './utils/cartUtils';

function Default() {
  const color = useSmartTextColor();

  const listColor = useSmartTextColor('#666');
  const { breakpoints } = usePageBreakpoints();
  const { configs, setShowIsCartBlockedModal, order, menuOpen } = useWebContext();
  const history = useHistory();
  const { setFunction } = useOrderContext();

  const textColor = useTextColor(configs.color);
  const { min_order_amount } = useCartContext();

  const [shipmentStep, setShipmentStep] = useState<1 | 2 | 3 | 4>(1);
  const [showSendOrderAlert, setShowSendOrderAlert] = useState<boolean>(false);
  const [showCartDeliveryAlert, setShowCartDeliveryAlert] = useState<boolean>(false);
  const [showPbmAlert, setShowPbmAlert] = useState<boolean>(false);
  const [pbmDiscount, setPbmDiscount] = useState<IAlert | null>(null);
  const [pbmProduct, setPbmProduct] = useState<ICartProduct | null | undefined>(null);

  const [showSteps, setShowSteps] = useState<boolean>(false);
  const cartModals = useCartModals();
  const cartData = useCartData();

  const redirectIfOrderClosed = useMemo(() => [OrderStatus.CLOSED, OrderStatus.CANCELED], []);

  const freeShippingProducts = useSelector<ApplicationState, IProductList>(
    (state) => state.productLists.freeShipping,
  );

  const paymentAlertAsks = useSelector<ApplicationState, IPaymentAlertAsks>(
    (state) => state.payment.asks,
  );

  useEffect(() => {
    if (order && !localStorage.getItem('last_paid_order')) {
      useInitiateCheckoutPixel(order);
    }
  }, [order?.id]);

  useCartEffects({
    order,
    paymentAlertAsks,
    setShowCartDeliveryAlert,
  });

  useEffect(() => {
    if (!menuOpen && cartModals.comboBottomSheetOpen) {
      cartModals.closeComboBottomSheet();
    }
  }, [menuOpen, cartModals.comboBottomSheetOpen]);

  useEffect(() => {
    if (cartData.productVariants && Object.keys(cartData.productVariants).length > 0) {
      cartModals.openComboDetails();
    }
  }, [cartData.productVariants]);

  const shipmentCardStyle = {
    backgroundColor: configs.color,
    borderColor: configs.color,
    color: textColor,
  };

  const openPbmAlert = useCallback((discount: IAlert, product?: ICartProduct) => {
    if (discount) {
      setPbmDiscount(discount);
      setPbmProduct(product);
      setShowPbmAlert(true);
    }
  }, []);

  const handleClosePbmAlert = useCallback(() => {
    setShowPbmAlert(false);
    setPbmDiscount(null);
    setPbmProduct(null);
  }, []);

  useEffect(() => {
    setFunction('openPbmAlert', openPbmAlert);
  }, [setFunction, openPbmAlert]);

  useEffect(() => {
    setFunction('openPbmAlert', openPbmAlert);
  }, [setFunction, openPbmAlert]);

  useEffect(() => {
    if (order && redirectIfOrderClosed.includes(order.status)) {
      window.location.href = '/';
    }
  }, [order, redirectIfOrderClosed]);

  const handleCloseSendOrderAlert = useCallback(
    () => setShowSendOrderAlert(false),
    [setShowSendOrderAlert],
  );

  const handleFinishOrder = useCallback(() => {
    const { isValid: orderCanBeChanged, showIsCartBlockedModal } = canChangeOrder({
      type: 'pedido',
      action: 'finalizado',
    });

    if (
      (order?.status !== OrderStatus.OPEN && order?.status !== OrderStatus.ABANDONED,
      order?.cart.products.some((item: any) => item.prescription_retention))
    ) {
      if (order) {
        usePurchasePixel(order);
      }
      return true;
    }

    if (orderCanBeChanged && canFinishOrder(shipmentStep)) {
      if (order) {
        usePurchasePixel(order);
      }

      return true;
    }

    if (showIsCartBlockedModal) setShowIsCartBlockedModal(true);

    return false;
  }, [
    order?.status,
    order?.cart.products,
    canChangeOrder,
    canFinishOrder,
    shipmentStep,
    setShowIsCartBlockedModal,
  ]);

  const handleCloseComboModal = useCallback(() => {
    cartModals.closeComboModal();
    cartData.clearProductVariants();
  }, [cartModals, cartData]);

  const handleCloseComboBottomSheet = useCallback(() => {
    cartModals.closeComboBottomSheet();
    cartData.clearProductVariants();
  }, [cartModals, cartData]);

  const proceedToCheckout = useCallback(() => {
    history.push('/checkout');
  }, [history]);

  const shouldShowSteps = useCallback(() => {
    setShowSteps(true);
  }, []);

  useEffect(() => {
    setFunction('handleFinishOrder', handleFinishOrder);
  }, [setFunction, handleFinishOrder]);

  const forwardedOrderAlertCallback = useCallback(
    (show: boolean) => setShowSendOrderAlert(show),
    [setShowSendOrderAlert],
  );

  useEffect(() => {
    setFunction('forwardedOrderAlert', forwardedOrderAlertCallback);
  }, [setFunction, forwardedOrderAlertCallback]);

  useEffect(() => {
    setFunction('proceedToCheckout', proceedToCheckout);
    setFunction('shouldShowSteps', shouldShowSteps);
  }, [setFunction, proceedToCheckout, shouldShowSteps]);

  useEffect(() => {
    if (order && shouldShowStatusSteps(order, showSteps)) {
      proceedToCheckout();
    }
  }, [showSteps, order]);

  return (
    <section className="Cart pb-3" style={{ overflowX: 'hidden' }}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col {...breakpoints} className="breakpoint-page">
            {order === null ? (
              <CartPageLoad />
            ) : (
              <>
                {order && order.cart.products.length > 0 ? (
                  <>
                    {shouldShowStatusSteps(order, showSteps) && (
                      <Row className="justify-content-center my-2">
                        <Col xs={12} md={10} lg={8}>
                          <StatusSteps order={order} />
                        </Col>
                      </Row>
                    )}
                    <div className="centered flex-column pt-3">
                      <div>
                        {!shouldShowStatusSteps(order, showSteps) && (
                          <CartContextStepper choosenStep="cart" />
                        )}
                      </div>
                      <Row className="justify-content-center w-100">
                        <Col xs={12} xl={8} md={8}>
                          <CartContent
                            shipmentCardStyle={shipmentCardStyle}
                            showCartDeliveryAlert={showCartDeliveryAlert}
                            order={order}
                            min_order_amount={min_order_amount}
                            combos={cartData.combos}
                            setEditProducts={cartData.setEditProducts}
                            setProductToCombo={cartData.setProductToCombo}
                          />
                          <AddMoreItems order={order} />
                        </Col>

                        <Col xs={12} md={4}>
                          <Shipment
                            order={order}
                            setShipmentStep={setShipmentStep}
                            handleFinishOrder={handleFinishOrder}
                            forwardedOrderAlert={setShowSendOrderAlert}
                            showPayment={false}
                          />
                        </Col>
                      </Row>
                    </div>

                    <Row>
                      <Col xs={12}>
                        <SendOrderAlert
                          show={showSendOrderAlert}
                          handleClose={handleCloseSendOrderAlert}
                          socialNetworkType={order.client.social_network_type}
                          clientIdentification={order.client.identification}
                        />
                      </Col>

                      <Col xs={12}>
                        <FreeShippingSection
                          freeShippingProducts={freeShippingProducts}
                          order={order}
                          listColor={listColor}
                        />
                        <PaidAlertModal />
                      </Col>
                    </Row>
                  </>
                ) : (
                  <EmptyCart color={color} />
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      <ComboBottomSheet
        open={cartModals.comboBottomSheetOpen}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        onClose={handleCloseComboBottomSheet}
        editProducts={cartData.editProducts}
      />
      <ComboModal
        show={cartModals.comboModalOpen}
        onClose={handleCloseComboModal}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        editProducts={cartData.editProducts}
      />
      <ComboAddModal
        show={cartModals.showComboAddModal}
        onClose={cartModals.closeComboAddModal}
        product={cartModals.modalAddProduct}
      />
      <ComboRemoveItemModal
        show={cartModals.comboRemoveItemModalOpen}
        onClose={cartModals.closeComboRemoveModal}
        product={cartModals.modalRemoveProduct}
      />
      <PbmAlert
        show={showPbmAlert}
        onHide={handleClosePbmAlert}
        discount={pbmDiscount}
        product={pbmProduct}
        orderId={order?.id || 0}
      />
    </section>
  );
}

export default Default;
